import React from "react";
import { renderToString } from "react-dom/server";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { extractTags } from "~/helpers/extract-tags";

type Props = {
  content: string;
  references?: any[];
};

const renderNodeToHTML = (
  node: any,
  index: number = 0,
  references: any[] = []
): string => {
  if (!node) return "";

  // Handle text nodes
  if (node.type === "text") {
    return node.value || "";
  }

  // Helper function to find a reference by ID
  const findReference = (id: string) => {
    return references.find(
      (ref) => ref.node?.yamlId === id || ref.node?.frontmatter?.id === id
    );
  };

  // Handle markdown AST nodes
  if (node.type === "heading") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";

    return `<h${node.depth}>${children}</h${node.depth}>`;
  }

  if (node.type === "paragraph") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<p>${children}</p>`;
  }

  if (node.type === "blockquote") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<blockquote>${children}</blockquote>`;
  }

  if (node.type === "list") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";

    if (node.ordered) {
      return `<ol>${children}</ol>`;
    } else {
      return `<ul>${children}</ul>`;
    }
  }

  if (node.type === "listItem") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<li>${children}</li>`;
  }

  if (node.type === "strong") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<strong>${children}</strong>`;
  }

  if (node.type === "emphasis") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<em>${children}</em>`;
  }

  if (node.type === "link") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<a href="${node.url}" title="${node.title || ""}">${children}</a>`;
  }

  if (node.type === "table") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<table>${children}</table>`;
  }

  if (node.type === "tableRow") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<tr>${children}</tr>`;
  }

  if (node.type === "tableCell") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<td>${children}</td>`;
  }

  // Handle raw HTML nodes that contain embedded components
  if (node.type === "html") {
    const htmlContent = node.value || "";

    // Check for EmbeddedAsset
    const assetMatch = htmlContent.match(
      /<EmbeddedAsset\s+id="([^"]+)"(?:\s+type="([^"]+)")?[^>]*\s*\/>/
    );
    if (assetMatch) {
      const [, id, type = "asset"] = assetMatch;
      const ref = findReference(id);

      if (!ref?.node) {
        return `<div>Embedded Asset: ${id} (Type: ${type}) - Not Found</div>`;
      }

      const asset = ref.node;
      const { url, title, file, description, contentType } = asset;
      const { tags, content } = extractTags(description || "");

      if (contentType && contentType.indexOf("image") !== -1) {
        const imageUrl = `${process.env.GATSBY_SITE_URL}${file.publicURL}`;
        let imageHTML = `<img src="${imageUrl}" alt="${title || ""}" />`;

        if (content) {
          imageHTML += `<p><em>${content}</em></p>`;
        }

        return `<div>${imageHTML}</div>`;
      }

      if (contentType && contentType.indexOf("video") !== -1) {
        const videoUrl = `${process.env.GATSBY_SITE_URL}${file.publicURL}`;
        let videoHTML = `<video autoplay muted loop playsinline><source src="${videoUrl}" type="${contentType}" /></video>`;

        if (content) {
          videoHTML += `<p><em>${content}</em></p>`;
        }

        return `<div>${videoHTML}</div>`;
      }

      return `<div>Asset: ${title || id} (Type: ${contentType || type})</div>`;
    }

    // Check for EmbeddedEntry
    const entryMatch = htmlContent.match(
      /<EmbeddedEntry\s+id="([^"]+)"(?:\s+type="([^"]+)")?[^>]*\s*\/>/
    );
    if (entryMatch) {
      const [, id, type = "entry"] = entryMatch;
      const ref = findReference(id);

      if (!ref?.node) {
        return `<div>Embedded Entry: ${id} (Type: ${type}) - Not Found</div>`;
      }

      const entry = ref.node;
      const entryType = entry.internal?.type || entry.__typename;

      // Handle different component types and render simplified versions for feeds
      if (entryType === "ComponentLinkYaml") {
        if (entry.url && entry.url.indexOf("www.youtube.com") !== -1) {
          return `<div><a href="${entry.url}" target="_blank">${
            entry.title || entry.url
          }</a></div>`;
        }
        return `<div><a href="${entry.url}" target="_blank">${
          entry.title || entry.url
        }</a></div>`;
      }

      if (entryType === "ComponentAppYaml") {
        return `<div><a href="${entry.url}" target="_blank">${
          entry.name || entry.title
        }</a></div>`;
      }

      if (entryType === "PageMdx") {
        return `<div><a href="${process.env.GATSBY_SITE_URL}${
          entry.frontmatter?.url?.pathname || ""
        }">${entry.frontmatter?.title || entry.title}</a></div>`;
      }

      if (entryType === "ComponentCalloutYaml") {
        return `<div><blockquote>${
          entry.content || entry.text
        }</blockquote></div>`;
      }

      if (entryType === "ComponentCodeYaml") {
        return `<pre><code>${entry.code}</code></pre>`;
      }

      if (entryType === "ComponentBlockquoteYaml") {
        return `<blockquote>${entry.quote}</blockquote>`;
      }

      if (entryType === "ComponentEmbedYaml") {
        if (entry.type === "Tweet") {
          return `<div>Tweet: <a href="${entry.url}" target="_blank">${entry.url}</a></div>`;
        }

        if (entry.type === "Podcast") {
          return `<div>Podcast: ${entry.title || entry.name}</div>`;
        }

        if (entry.type === "Spotify") {
          return `<div>Spotify: ${entry.title || entry.name}</div>`;
        }

        if (entry.type === "Figma") {
          return `<div>Figma: <a href="${entry.url}" target="_blank">${
            entry.title || entry.url
          }</a></div>`;
        }

        // Fallback for other embeds
        return `<div>${entry.code?.code || entry.code || ""}</div>`;
      }

      // Fallback for other entry types
      return `<div>Embedded Entry: ${entry.title || entry.name || id} (Type: ${
        entryType || type
      })</div>`;
    }

    // Check for InlineLink
    const inlineLinkMatch = htmlContent.match(
      /<InlineLink\s+id="([^"]+)"(?:\s+type="([^"]+)")?[^>]*\s*\/>/
    );
    if (inlineLinkMatch) {
      const [, id, type = "inline-link"] = inlineLinkMatch;
      const ref = findReference(id);

      if (!ref?.node) {
        return `Inline Link: ${id} (Type: ${type}) - Not Found`;
      }

      const entry = ref.node;
      const entryType = entry.internal?.type || entry.__typename;

      if (entryType === "ComponentInlineAffiliateLinkYaml") {
        return `<a href="${entry.url}" target="_blank">${
          entry.title || entry.name
        }</a>`;
      }

      if (entryType === "ComponentInlineTagYaml") {
        return `<span>${entry.name}</span>`;
      }

      return "";
    }

    // If it's other HTML content, return it as-is
    return htmlContent;
  }

  // Handle root and other container nodes
  if (node.type === "root" || node.type === "html" || node.type === "body") {
    return (
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || ""
    );
  }

  // Handle HTML element nodes (from our manual transformation)
  if (node.type === "htmlElement") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<${node.tagName}>${children}</${node.tagName}>`;
  }

  // Handle HTML element nodes (from rehype processing)
  if (node.type === "element") {
    const children =
      node.children
        ?.map((child: any, childIndex: number) =>
          renderNodeToHTML(child, childIndex, references)
        )
        .join("") || "";
    return `<${node.tagName}>${children}</${node.tagName}>`;
  }

  // Handle doctype and other non-renderable nodes
  if (node.type === "doctype" || node.type === "comment") {
    return "";
  }

  // Fallback for unknown node types
  return "";
};

const transformHtmlNodesInMarkdown = (children: any[]): any[] => {
  const result = [];
  let i = 0;

  while (i < children.length) {
    const node = children[i];

    // Check if this is an opening HTML tag in markdown AST
    if (node.type === "html" && node.value.match(/^<(\w+)>$/)) {
      const tagMatch = node.value.match(/^<(\w+)>$/);
      const tagName = tagMatch[1];
      const closingTag = `</${tagName}>`;

      // Find the matching closing tag
      let closingIndex = -1;
      for (let j = i + 1; j < children.length; j++) {
        if (children[j].type === "html" && children[j].value === closingTag) {
          closingIndex = j;
          break;
        }
      }

      if (closingIndex !== -1) {
        // Collect all nodes between opening and closing tags
        const elementChildren = [];
        for (let k = i + 1; k < closingIndex; k++) {
          elementChildren.push(children[k]);
        }

        // Create new element node (markdown-style but with HTML structure)
        const elementNode = {
          type: "htmlElement",
          tagName: tagName,
          properties: {},
          children: transformHtmlNodesInMarkdown(elementChildren), // Recursively transform children
          position: {
            start: node.position?.start,
            end: children[closingIndex].position?.end,
          },
        };

        result.push(elementNode);
        i = closingIndex + 1; // Skip past the closing tag
      } else {
        // No matching closing tag found, treat as regular html node
        result.push(node);
        i++;
      }
    } else {
      // Regular node, just add it
      result.push(node);
      i++;
    }
  }

  return result;
};

const feedContent = (content: string, references: any[] = []) => {
  // Parse markdown content using the same logic as markdown.tsx
  const file = unified().use(remarkParse).use(remarkGfm).parse(content);

  // Apply our manual HTML transformation to the markdown AST
  const walkAndTransform = (node: any): any => {
    if (node.children) {
      node.children = transformHtmlNodesInMarkdown(node.children);
      node.children.forEach(walkAndTransform);
    }
    return node;
  };

  const transformedFile = walkAndTransform(file);

  // Render the parsed content to HTML string
  return renderNodeToHTML(transformedFile, 0, references);
};

export default feedContent;
